import React from "react"
import { mapDispatchProps, mapStateToProps } from "../../../store/store"
import { connect } from "react-redux"
import { X } from "react-feather"
import Divider from "../../../components/divider"
import dayjs from "../../../plugins/dayjs"
import Button from "../../../components/button"
import { debounce } from "../../../helpers/debounce"
import { CAMPAIGN_PAYMENT_TYPE } from "../../../api/repositories/campaignPayments"
import { CAMPAIGN_PAYMENT_DTO_ENUMS } from "api/repositories/campaignPayments/campaignPayments.constants"
import CampaignPaymentCancelConfirm from "./cancel/confirm"
import { useLanguage } from "@languages/index"

interface Props {
  className?: string
  onClick?: any

  style?: any
  history?: any
  preferences?: any
  settings?: any

  auth?: any
  brand?: any

  setPopup?: (params?: any) => void
  showSheet?: (params?: any) => void
  showOverlays?: (params?: any) => void
  closeOverlays?: () => void
}

const CampaignPaymentBills = (props: Props) => {
  const language = useLanguage()
  const languageTexts = { 
    ...language.common, 
    ...language.database,
    ...language.pages.campaigns
  }

  const showCancelConfirm = () => {
    props.closeOverlays()
    debounce(() => {
      props.showOverlays({
        content: () => <CampaignPaymentCancelConfirm />,
      })
    }, 400)
  }

  const renderPurchasedMethod = (purchasedMethod?:string, billingType?:string) => {
    if (!purchasedMethod && !billingType) 
      return null

    const splittedMethods = purchasedMethod ? purchasedMethod.split(",") : []
    const cardOrCashText = splittedMethods[0] != null ? splittedMethods[0].trim() 
      : billingType == CAMPAIGN_PAYMENT_DTO_ENUMS.BILLING_TYPE.FREE ? languageTexts.campaign_payments.billing_type.FREE
      : null
    const cardNameText = splittedMethods[1] != null ? splittedMethods[1].trim() : null

    if (cardOrCashText == null && cardNameText == null) 
      return null

    return (
      <>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col justify-start">
            <span className="font-bold text-sm tracking-tighter">{languageTexts.payment_method}</span>
          </div>

          <div className="flex flex-col justify-center gap-1">
            {cardOrCashText != null && (
              <span className="font-bold text-sm tracking-tighter text-right">
                {cardOrCashText}
              </span>
            )}
            {cardNameText != null && (
              <span className="text-sm tracking-tighter text-right opacity-50">
                {cardNameText}
              </span>
            )}
          </div>
        </div>

        <Divider style={{ borderColor: "rgba(0,0,0,0.1)" }} className="my-5" />
      </>
    )
  }

  let containerStyle = ""
  if (props.preferences.isMobile) {
    containerStyle = "flex flex-col bg-white py-10 px-5 overflow-scroll min-w-[100vw]"
  } else {
    containerStyle = "flex flex-col bg-white rounded-2xl overflow-visible w-[480px] px-10 py-10"
  }

  return (
    <div className={`${containerStyle}`}>
      <div className="flex flex-row justify-between mb-9">
        <span className="font-bold text-xl tracking-tighter">{languageTexts.bills}</span>
        <span
          className="text-sm tracking-tighter underline cursor-pointer"
          onClick={() => {
            props.closeOverlays()
          }}
        >
          <X />
        </span>
      </div>

      <div className="flex flex-row justify-between">
        <div className="flex flex-col justify-start">
          <span className="font-bold text-sm tracking-tighter">{languageTexts._cost(props.history.typeDesc)}</span>
        </div>

        {props.history.type == "SINGLE" ? (
          <div className="flex flex-col justify-center gap-1">
            <span className="font-bold text-sm tracking-tighter text-right">
              {languageTexts._amount(props.history.amount)}
            </span>
            <span className="font-bold text-sm tracking-tighter text-right text-[#ED8E00]">
              {"(" +
                languageTexts._price_per_1ea(props.history.price.toLocaleString(), props.settings.currencyText) +
                ")"}
            </span>
          </div>
        ) : (
          <div className="flex flex-col justify-center gap-1">
            <span className="font-bold text-sm tracking-tighter text-right">
              {languageTexts._amount(props.history.amount)}
            </span>
            <span className="font-bold text-sm tracking-tighter text-right text-[#ED8E00]">
              {languageTexts._price(props.history.priceTotal.toLocaleString(), props.settings.currencyText)}
            </span>
          </div>
        )}
      </div>

      <Divider style={{ borderColor: "rgba(0,0,0,0.1)" }} className="my-5" />

      {/* 일회성 제품 구매 내역 */}
      {
        [
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.SINGLE,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.CANCEL,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.COMMISSION,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.COMMISSION_INCREASE,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.INSPECTION_FEE,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.VIDEO_CLEAN,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_FULL,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_PARTIAL,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_SINGLE,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_COMMISSION,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_COMMISSION_INCREASE,
          CAMPAIGN_PAYMENT_DTO_ENUMS.TYPE.REFUND_INSPECTION_FEE,
        ].includes(props.history.type) ? (
        <div className="flex flex-col">
          <div className="flex flex-row justify-between">
            <div className="flex flex-col justify-start">
              <span className="font-bold text-sm tracking-tighter">{languageTexts.paid_date}</span>
            </div>

            <div className="flex flex-col justify-center gap-1">
              <span className="font-bold text-sm tracking-tighter text-right">{`${dayjs(props.history.createdAt).format(
                "YYYY-MM-DD hh:mm"
              )}`}</span>
            </div>
          </div>
        </div>
      ) : null}

      {/* 시딩 패스 구매 내역*/}
      {props.history.type == CAMPAIGN_PAYMENT_TYPE.MONTHLY || props.history.type == CAMPAIGN_PAYMENT_TYPE.YEARLY ? (
        <div className="flex flex-col gap-[0.625rem]">
          <div className="flex flex-row justify-between">
            <div className="flex flex-col justify-start">
              <span className="font-bold text-sm tracking-tighter">{languageTexts.last_paid_date}</span>
            </div>

            <div className="flex flex-col justify-center gap-1">
              <span className="font-bold text-sm tracking-tighter text-right">
                {`${dayjs(props.history.createdAt).format("YYYY-MM-DD hh:mm")}`}
              </span>
            </div>
          </div>

          <div className="flex flex-row justify-between">
            <div className="flex flex-col justify-start">
              <span className="font-bold text-sm tracking-tighter">
                {props.history.isSeedingPassCancel ? languageTexts.expiration_date : languageTexts.next_payment_date}
              </span>
            </div>

            <div className="flex flex-col justify-center gap-1">
              <span className="font-bold text-sm tracking-tighter text-right">
                {`${dayjs(props.history.expiresAt).add(1, "day").format("YYYY-MM-DD 09:00")}`}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <Divider style={{ borderColor: "rgba(0,0,0,0.1)" }} className="my-5" />

      {renderPurchasedMethod(props.history.purchasedMethod, props.history.billingType)}

      <div className="flex flex-row justify-between">
        <div className="flex flex-col justify-start">
          <span className="font-bold text-sm tracking-tighter">{languageTexts.total_price}</span>
        </div>

        {props.history.type == CAMPAIGN_PAYMENT_TYPE.SINGLE || props.history.type == CAMPAIGN_PAYMENT_TYPE.CANCEL ? (
          <>
            <div className="flex flex-col justify-center gap-1">
              <span className="font-bold text-sm tracking-tighter text-right">
                {languageTexts._price(
                  (props.history.amount * props.history.price * 1.1).toLocaleString(),
                  props.settings.currencyText
                )}
              </span>
              <span className="font-bold text-sm tracking-tighter text-right opacity-50">{`(${props.history.price.toLocaleString()}*${props.history.amount.toLocaleString()}) + VAT(10%)`}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col justify-center gap-1">
              <span className="font-bold text-sm tracking-tighter text-right">
                {languageTexts._price((props.history.price * props.history.amount * 1.1).toLocaleString(), props.settings.currencyText)}
              </span>
              <span className="font-bold text-sm tracking-tighter text-right opacity-50">{`(${props.history.price.toLocaleString()} * ${props.history.amount.toLocaleString()}) + VAT(10%)`}</span>
            </div>
          </>
        )}
      </div>

      {((props.history.type == CAMPAIGN_PAYMENT_TYPE.MONTHLY || props.history.type == CAMPAIGN_PAYMENT_TYPE.YEARLY) &&
      props.brand.isSeedingPassAutoPurchaseEnabled) ? (
        <div className="mt-5">
          <Button
            bordered
            borderColor={"BLACK"}
            text={languageTexts.payments_bills_text_1}
            onClick={() => {
              showCancelConfirm()
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(CampaignPaymentBills)
